import type { Injector } from '@angular/core';
import type {
  AbstractControl,
  AsyncValidatorFn,
  ValidationErrors,
} from '@angular/forms';
import type { OnyxPhone } from '@onyx/angular';
import { ONYX_INPUT_DEBOUNCE_TIME, OnyxPhonePipe } from '@onyx/angular';
import { isValidPhoneNumber } from 'libphonenumber-js';
import type { Observable } from 'rxjs';
import { map, of, switchMap, timer } from 'rxjs';
import { DriversService } from '../services/drivers.service';

export interface PrivatePhoneLocalConflict {
  type: 'local';
  phoneNumber: string;
  driverUuid: string;
}

export interface PrivatePhoneGlobalConflict {
  type: 'global';
  phoneNumber: string;
}

export type PrivatePhoneConflict =
  PrivatePhoneLocalConflict | PrivatePhoneGlobalConflict;

export const uniquePrivatePhoneValidator =
  (
    injector: Injector,
    options?: { excludeDriverUuid?: () => string | null | undefined },
  ): AsyncValidatorFn =>
  (
    control: AbstractControl<OnyxPhone | null>,
  ): Observable<ValidationErrors | null> => {
    const phone = control.value;
    if (!phone) return of(null);

    const phoneNumber = `${phone.areaCode}${phone.number}`;
    if (!isValidPhoneNumber(phoneNumber)) return of(null);

    const driversService = injector.get(DriversService);
    const phonePipe = injector.get(OnyxPhonePipe);

    return timer(ONYX_INPUT_DEBOUNCE_TIME).pipe(
      switchMap(() => driversService.checkPrivatePhone(phone)),
      map((result) => {
        if (!result.conflict) return null;

        const exclueDriverUuid = options?.excludeDriverUuid?.();
        if (result.driverUuid === exclueDriverUuid) return null;

        const formattedPhoneNumber = phonePipe.transform(phone);

        if (result.local) {
          return {
            privatePhoneConflict: {
              type: 'local',
              phoneNumber: formattedPhoneNumber,
              driverUuid: result.driverUuid,
            } satisfies PrivatePhoneLocalConflict,
          };
        }

        return {
          privatePhoneConflict: {
            type: 'global',
            phoneNumber: formattedPhoneNumber,
          } satisfies PrivatePhoneGlobalConflict,
        };
      }),
    );
  };
